import rtkApis from '@rtkApis';
import parentApi from '../store/parentApi';
import { isKeycloakDisabledForPath } from '@/utility/helpers';

const postListingApis = parentApi.injectEndpoints({
  endpoints: (build) => ({
    postNewListing: build.mutation({
      query: (params) => rtkApis.postNewListing.query(params),
    }),
    getListingDetail: build.query({
      query: (params) => rtkApis.getListingDetail.query(params),
      transformResponse: (res, meta, args) => rtkApis.getListingDetail.transformer(res, meta, args),
    }),
    getUpsellDetail: build.query({
      queryFn: (params, api, extraOptions, baseQuery) =>
        rtkApis.getUpsellDetail.queryFn(params, api, extraOptions, baseQuery),
    }),
    getPriceCheck: build.query({
      query: (params) => rtkApis.getPriceCheck.query(params),
    }),
    getLicenseValidity: build.query({
      query: (adLicense) => `/api/surge/listings/new_listing_data?ad_license_number=${adLicense}`,
    }),
    getAutoGeneratedListingContent: build.mutation({
      query: (body) => rtkApis.getAutoGeneratedListingContent.query(body),
    }),
    updateListing: build.mutation({
      query: (params) => rtkApis.updateListing.query(params),
      invalidatesTags: (result, error, arg) => {
        if (error || !result) return [];
        const tags = [];
        if (!arg?.skipMyListingsInvalidate) tags.push('my-listings');
        if (!isKeycloakDisabledForPath()) tags.push('current');
        return tags;
      },
    }),
    getLocationsBasedOnNational: build.query({
      query: (nationalAddress) => `/api/surge/locations/google_location?address=${nationalAddress}`,
    }),
    postSavedListing: build.mutation({
      query: (query) => rtkApis.postSavedListing.query(query),
      async onQueryStarted(arg, api) {
        rtkApis.postSavedListing?.afterSuccess(arg, api);
      },
      transformResponse: (response, meta, arg) => rtkApis.postSavedListing.transformer(response, meta, arg),
    }),
    hideUnhideListings: build.mutation({
      query: (query) => rtkApis.hideUnhideListings.query(query),
      async onQueryStarted(arg, api) {
        rtkApis.hideUnhideListings?.afterSuccess(arg, api);
      },
      transformResponse: (response) => rtkApis.hideUnhideListings.transformer(response),
    }),
    getAmenities: build.query({
      query: (purposeId) => rtkApis.getAmenities.query(purposeId),
      transformResponse: (response) => rtkApis.getAmenities.transformer(response),
    }),
    // The same response unmapped ({ amenities: [{ type_id, groups: [{ features }] }] }), for
    // matching amenities by their English title. getAmenities maps titles to localised labels.
    getAmenitiesList: build.query({
      query: (purposeId) => rtkApis.getAmenities.query(purposeId),
    }),
    getQualityCriteria: build.query({
      query: (params) => rtkApis.getQualityCriteria.query(params),
      transformResponse: (response) => rtkApis.getQualityCriteria.transformer(response),
    }),
    fetchCrossCityDetail: build.query({
      query: (params) => rtkApis.fetchCrossCityDetail.query(params),
      transformResponse: (response) => rtkApis.fetchCrossCityDetail.transformer(response),
    }),
    getApplicableLicenses: build.query({
      query: (params) => '/api/surge/licenses/applicable_licenses',
      transformResponse: (response) =>
        response?.data?.licenses?.length ? response?.data?.licenses?.map((e) => ({ ...e, key: e?.id })) : [],
    }),
    sendLicenseOtp: build.mutation({
      query: ({ listing_id, user_id, identifier }) => ({
        url: '/api/surge/otps',
        method: 'POST',
        body: {
          channel: 'phone',
          purpose: 'post_listing',
          entity_type: 'Listing',
          entity_id: listing_id,
          user_id,
          identifier,
        },
      }),
    }),
    verifyLicenseOtp: build.mutation({
      query: ({ otp, reference_number }) => ({
        url: '/api/surge/otps/verify',
        method: 'POST',
        body: {
          code: otp,
          reference_number,
          purpose: 'post_listing',
        },
      }),
      transformErrorResponse: (response) => response,
    }),
    getListingCategories: build.query({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        if (!rtkApis.getListingCategories?.query) {
          return { data: [] };
        }
        const query = rtkApis.getListingCategories.query(arg);
        const result = await baseQuery(query);
        if (result.error) return { error: result.error };
        const transformed = rtkApis.getListingCategories.transformer?.(result.data, result.meta, arg) ?? result.data;
        return { data: transformed };
      },
    }),
    getSurgeFeatures: build.query({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        if (!rtkApis.getSurgeFeatures?.query) {
          return { data: null };
        }
        const query = rtkApis.getSurgeFeatures.query(arg);
        if (!query) {
          return { data: null };
        }
        const result = await baseQuery(query);
        if (result.error) return { error: result.error };
        const data = rtkApis.getSurgeFeatures.transformer
          ? rtkApis.getSurgeFeatures.transformer(result.data)
          : result.data;
        return { data };
      },
    }),
  }),
});

export const {
  usePostNewListingMutation,
  useLazyGetPriceCheckQuery,
  useHideUnhideListingsMutation,
  usePostSavedListingMutation,
  useLazyGetListingDetailQuery,
  useGetListingDetailQuery,
  useLazyGetLicenseValidityQuery,
  useGetAutoGeneratedListingContentMutation,
  useUpdateListingMutation,
  useLazyGetLocationsBasedOnNationalQuery,
  useLazyGetAmenitiesQuery,
  useLazyGetAmenitiesListQuery,
  useLazyFetchCrossCityDetailQuery,
  useLazyGetUpsellDetailQuery,
  useGetUpsellDetailQuery,
  useGetApplicableLicensesQuery,
  useGetQualityCriteriaQuery,
  useSendLicenseOtpMutation,
  useVerifyLicenseOtpMutation,
  useGetListingCategoriesQuery,
  useGetSurgeFeaturesQuery,
  useLazyGetSurgeFeaturesQuery,
} = postListingApis;
